import { useCallback, useEffect, useRef, useState } from 'react';
import { DisclosureCollapse, DisclosureExpand } from '@sas/react-svg';
import { useConfig } from '../../hooks/useConfig';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { Button, ButtonVariant } from '../Button';
import { Checkbox } from '../Checkbox';
import { BaseDialog } from '../Dialog/__internal__/';
import { Flow } from '../Layouts/Flow';
import { useSuppressMessagesGlobalSetting } from '../Settings/GlobalSettingsProvider';
import { Text } from '../Text';
import { TextArea } from '../TextArea';
import bundle from './NovaComponents-MessageDialog-gui-icu.properties';

// Import CSS last
import cn from './MessageDialog.css.json';
import './MessageDialog.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);

// this hook provides a way to share common localized strings
export function useMessageDialogText() {
  const localize = useTextLocalizer();
  return {
    OK: localize('messageDialog.actions.ok.label'),
    CANCEL: localize('messageDialog.actions.cancel.label'),
    YES: localize('messageDialog.actions.yes.label'),
    NO: localize('messageDialog.actions.no.label'),
    ABORT: localize('messageDialog.actions.abort.label'),
    RETRY: localize('messageDialog.actions.retry.label'),
    IGNORE: localize('messageDialog.actions.ignore.label'),
    CLOSE: localize('messageDialog.actions.close.label'),
    DELETE: localize('messageDialog.actions.delete.label'),
    SAVE: localize('messageDialog.actions.save.label'),
    DONT_SAVE: localize('messageDialog.actions.dontSave.label'),
    DISCARD: localize('messageDialog.actions.discard.label'),
    APPLY: localize('messageDialog.actions.apply.label'),
    RESET: localize('messageDialog.actions.reset.label'),
    CLOSE_TAB: localize('messageDialog.actions.closeTab.label'),
    CLOSE_TABS: localize('messageDialog.actions.closeTabs.label'),
    CONTINUE: localize('messageDialog.actions.continue.label')
  };
}

/**
 * Implements the "Don't Show Again" functionality.
 *
 * - Invoke in the parent scope of the target MessageDialog
 *   - `includeDontShowAgainCheckbox` prop must be set to `true`
 * - Return object keys:
 *   - `isSuppressed` - indicates if the MessageDialog is present in the preference setting
 *     - use when setting the `isOpen` prop
 *   - `persistDontShowAgainCheckbox` - writes the id to the preference setting
 *   - `onDontShowAgainCheckboxPress` - invoked when the checkbox value has changed
 *     - pass as a prop to MessageDialog
 *   - `dontShowAgainCheckboxValue` - state for the value of the checkbox
 *     - pass as a prop to MessageDialog
 *
 * @param id Globally uniquely identifies the message. Ex: mySolution-confirmFoo
 */
export function useDontShowAgainCheckbox(id) {
  const {
    suppressedMessages,
    updateSuppressedMessages
  } = useSuppressMessagesGlobalSetting();
  const [dontShowAgainCheckboxValue, setDontShowAgainCheckboxValue] = useState(false);
  return {
    isSuppressed: suppressedMessages.includes(id),
    onDontShowAgainCheckboxPress: () => {
      setDontShowAgainCheckboxValue(prev => !prev);
    },
    persistDontShowAgainCheckbox: () => {
      if (dontShowAgainCheckboxValue === true) {
        updateSuppressedMessages(suppressedMessages.concat(id));
        setDontShowAgainCheckboxValue(false);
      }
    },
    dontShowAgainCheckboxValue
  };
}
const useTextId = createUseHtmlId('messagedialog-text');

/**
 * A wrapper around dialog for specific messages
 * @see https://rndconfluence.sas.com/confluence/display/NOVA/MessageDialog
 */
export const MessageDialog = props => {
  const textId = useTextId();
  const localize = useTextLocalizer();
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const textAreaRef = useRef(null);
  const configObj = useConfig();
  const {
    text,
    isOpen,
    title,
    type,
    detailText,
    includeDontShowAgainCheckbox,
    dontShowAgainCheckboxValue,
    onDontShowAgainCheckboxPress,
    onOpen: onOpenProp,
    onClose: onCloseProp,
    children,
    ...restProps
  } = props;
  const [textTabIndex, setTextTabIndex] = useState(undefined);
  const onOpen = useCallback(() => {
    setTextTabIndex(0);
    onOpenProp?.();
  }, [onOpenProp]);
  const onClose = useCallback(() => {
    setTextTabIndex(undefined);
    if (dontShowAgainCheckboxValue) onDontShowAgainCheckboxPress?.();
    onCloseProp?.();
  }, [dontShowAgainCheckboxValue, onCloseProp, onDontShowAgainCheckboxPress]);

  // the details should always be closed when the dialog opens
  useEffect(() => {
    if (!isOpen) setDetailsExpanded(false);
  }, [isOpen]);

  // if the textarea is expanded, give it focus
  useEffect(() => {
    if (detailsExpanded) textAreaRef.current?.focus();
  }, [detailsExpanded]);
  const detailsButtonText = detailsExpanded ? localize('messageDialog.hideDetails.label') : localize('messageDialog.showDetails.label');
  const detailsButtonIcon = detailsExpanded ? DisclosureCollapse : DisclosureExpand;
  let footerControls;
  if (detailText) {
    footerControls = /*#__PURE__*/_jsx(Button, {
      variant: ButtonVariant.LightSmall,
      onPress: () => {
        setDetailsExpanded(!detailsExpanded);
      },
      icon: {
        icon: detailsButtonIcon
      },
      text: detailsButtonText
    });
  }
  let accentFromType;
  switch (type) {
    case 'information':
      accentFromType = 'info';
      break;
    case 'warning':
      accentFromType = 'warning';
      break;
    case 'error':
      accentFromType = 'alert';
      break;
  }
  const maxHeightInPixels = Math.floor(0.75 * (document.documentElement.clientHeight ?? 0));
  return /*#__PURE__*/_jsx(BaseDialog, {
    ...restProps,
    isOpen: isOpen,
    title: title ?? configObj?.get('appName')?.replaceAll?.('®', ''),
    hideCloseIcon: true,
    footerControls: footerControls,
    role: "alertdialog",
    ariaDescribedBy: textId,
    accent: accentFromType,
    size: {
      minHeight: '11rem',
      minWidth: '24rem',
      maxWidth: '30rem',
      maxHeight: `${maxHeightInPixels}px`
    },
    onOpen: onOpen,
    onClose: onClose,
    children: /*#__PURE__*/_jsxs(Flow, {
      orientation: "vertical",
      spacing: "s",
      children: [/*#__PURE__*/_jsx(Text, {
        id: textId,
        tabIndex: textTabIndex,
        style: {
          outlineOffset: '0',
          margin: 'var(--focus-width)'
        },
        children: text
      }), detailText && detailsExpanded && /*#__PURE__*/_jsx("div", {
        className: cn.textAreaContainer,
        children: /*#__PURE__*/_jsx(TextArea, {
          ref: textAreaRef,
          ariaLabel: localize('messageDialog.details.aria'),
          width: "100%",
          value: detailText,
          readOnly: true,
          tabIndex: 0,
          style: {
            lineHeight: 1.4
          },
          autoExpand: true,
          maxRows: 10,
          onChange: () => {}
        })
      }), includeDontShowAgainCheckbox && (type === 'information' || type === 'warning') ? /*#__PURE__*/_jsx(Checkbox, {
        label: localize('messageDialog.dontShowThisMessageAgain.label'),
        checked: dontShowAgainCheckboxValue ?? false,
        onPress: onDontShowAgainCheckboxPress
      }) : null, /*#__PURE__*/_jsx("div", {
        children: children
      })]
    })
  });
};
//# sourceMappingURL=MessageDialog.js.map